/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */


import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpStatusCode } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {IndustryQueryRequestDTO} from '../models/industry-query-request-dto.model';
import {IndustryScenario} from '../models/industry-scenario-dto.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})

export class IndustryScenarioService {

  constructor(protected readonly configService: ConfigService,
              readonly http: HttpClient) {
  }

  /**
   * Sends a get request to retrieve list of all industry scenario categories in the database.
   */
  getAllIndustryScenarios(): Observable<any> {
    return this.http.get<any>(`${this.configService.baseApiUrl}industry-scenarios`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Send a get request to retrieve list of fcorgs by GMID
   * @param gmid GMID
   */
  getFcorgsByGmid(gmid: string): Observable<string[]> {
    const params = new HttpParams().set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}fcorgs/filter`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Send a get request to retrieve a list of industry scenarios by forecast organization
   * @param fcorg forecast organization
   */
  getIndustryScenariosByFcorg(fcorg: string): Observable<any> {
    const params = new HttpParams().set('fcorg', fcorg);
    return this.http.get<any>(`${this.configService.baseApiUrl}industry-scenarios`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of all industry scenarios with associations
   */
  getIndustryScenariosWithAssociations(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}industry-scenarios/associations`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve a filtered list from industry scenario categories in the database.
   * @param fcorg forecast organization
   * @param hidden hidden flag
   */
  findAllByFcorgAndHidden(fcorg: string, hidden: boolean): Observable<any> {
    const params = new HttpParams()
    .set('fcorg', fcorg)
    .set('hidden', hidden);
    return this.http.get<IndustryScenario[]>(`${this.configService.baseApiUrl}industry-scenarios`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Send a get request to retrieve a list of all scenario categories
   */
  getAllScenarioCategories(): Observable<any> {
    return this.http.get<any>(`${this.configService.baseApiUrl}manage-scenario-category-list`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Send a get request to retrieve a list of all scenario types
   */
  getAllScenarioTypes(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}manage-scenario-type-list`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get base industry scenarios by hidden and user ID
   */
  getIndustryScenarioByHiddenAndGmid(hidden: boolean, gmid: string): Observable<any> {
    const params = new HttpParams()
    .set('hidden', hidden)
    .set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}industry-scenarios`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get base industry scenarios by hidden and user ID
   */
  getIndustryScenarioByGmid(gmid: string): Observable<any> {
    const params = new HttpParams()
      .set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}industry-scenarios`, {params})
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Add industry scenario based off of a Global Scenario
   */
  createScenarioFromGlobalScenario(description: string, assumption: string, scenCategory: string,
                                   scenType: string, gmid: string, scenID: string, fcorg: string): Observable<any> {
    const params = new HttpParams()
    .set('description', description)
    .set('assumption', assumption)
    .set('scenCategory', scenCategory)
    .set('scenType', scenType)
    .set('gmid', gmid)
    .set('scenID', scenID)
    .set('fcorg', fcorg);
    return this.http.post<any>(`${this.configService.baseApiUrl}industry-scenarios/from-global`, null, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Creates an Industry Scenario off of another Industry Scenario as a base
   */
  createScenarioFromScenario(description: string, assumption: string, scenCategory: string,
                             scenType: string, gmid: string, oldID: string): Observable<any> {
    const params = new HttpParams()
    .set('description', description)
    .set('assumption', assumption)
    .set('scenCategory', scenCategory)
    .set('scenType', scenType)
    .set('gmid', gmid)
    .set('oldID', oldID);
    return this.http.post<any>(`${this.configService.baseApiUrl}industry-scenarios/from-scenario`, null, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Send a put request to update the changes made to the industry scenario
   * @param industryScenario industry scenario
   */
  updateIndustryScenario(industryScenario: IndustryScenario): Observable<IndustryScenario> {
    return this.http.put<IndustryScenario>(`${this.configService.baseApiUrl}industry-scenarios`, industryScenario);
  }

  /**
   * Send a delete request to delete the industry scenario by id
   * @param industryScenarioId industry scenario id
   */
  deleteIndustryScenario(industryScenarioId: number): Observable<any> {
    const params = new HttpParams().set('industryScenarioId', industryScenarioId);
    return this.http.delete(`${this.configService.baseApiUrl}industry-scenarios`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Creates an Industry Scenario from a Query Industry template Excel file
   * @param file Excel file
   * @param sheetList a list containing the selected sheets to read data from
   * @param gmid The users gmid
   * @param description The name of the scenario
   * @param assumption The scenario assumption
   * @param fcorg The scenario fcorg
   * @param scenCategory The scenario category
   * @param scenType The scenario type
   */
  createScenarioFromTemplate(file: File, sheetList: string[],
                             gmid: string, description: string,
                             assumption: string, fcorg: string, scenCategory: string, scenType: string): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    formData.append('gmid', gmid);
    formData.append('description', description);
    formData.append('assumption', assumption);
    formData.append('fcorg', fcorg);
    formData.append('scenCategory', scenCategory);
    formData.append('scenType', scenType);
    return this.http.post<any>(`${this.configService.baseApiUrl}industry-scenarios/from-template`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Send a request to update a specified Industry Scenario with the latest segment scheme
   * @param gmid gmid
   * @param industryScenarioId industry scenario id
   */
  updateIndustryScenarioWithSegmentScheme(gmid: string, industryScenarioId: number): Observable<any> {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('industryScenarioId', industryScenarioId.toString());
    return this.http.post<any>(`${this.configService.baseApiUrl}industry-scenarios/segment-scheme`, null,
      {params});
  }


  /**
   * Get the Start year and End Year of a vehicle scenario based off the industry scenario details
   * @param indScenId the Industry Scenario id
   */
  getYearRange(indScenId: number): Observable<any> {
    const params = new HttpParams()
    .set('indScenId', indScenId);
    return this.http.get<any>(`${this.configService.baseApiUrl}industry-scenarios/year-range`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Runs the query report
   * @param requestDTO the DTO housing all the query parameters
   * @return A query workbook
   */
  queryIndustry(requestDTO: IndustryQueryRequestDTO) {
    const headers = new HttpHeaders();
    return this.http.post(`${this.configService.baseApiUrl}industry-scenarios/query`,
      requestDTO, {headers, responseType: 'blob'}).pipe(catchError(error => throwError(error)));
  }

  /**
   * Validates the proposed changes to the industry scenario in the file
   * @param file The file with the changes
   * @param sheetName The name of the sheet to use
   * @param gmid The users gmid
   */
  queryIndustrySaveValidate(file: File, sheetName: string,
                            gmid: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    formData.append('gmid', gmid);
    return this.http.post(`${this.configService.baseApiUrl}industry-scenarios/query/save/validate`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the scenario from the template
   * @param file The template file
   * @param sheetName The name of the sheet to use
   */
  getScenarioFromTemplate(file: File, sheetName: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    return this.http.post(`${this.configService.baseApiUrl}industry-scenarios/forecast-template/scenario`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Saves the changes to the industry scenario in the file
   * @param file The file with the changes
   * @param sheetName The name of the sheet to use
   * @param gmid The users gmid
   */
  queryIndustrySave(file: File, sheetName: string,
                    gmid: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    formData.append('gmid', gmid);
    return this.http.post(`${this.configService.baseApiUrl}industry-scenarios/query/save`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Runs the global industry consolidation report
   *
   * @param scenIds      the ID's of the scenarios for the report
   * @param startYear    The start year for the report
   * @param endYear      The end year for the report
   * @param gmid         The user's GMID
   * @param aggregateTos A string flag to aggregate by TOS or not
   * @param fcorg the fcorg for the report, 'all' if multiple
   */
  getConsolidationReport(scenIds: string, startYear: number, endYear: number, gmid: string, aggregateTos: boolean,
                         fcorg: string) {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('fcorg', fcorg)
    .set('scen-ids', scenIds)
    .set('start-year', startYear)
    .set('end-year', endYear)
    .set('tos', aggregateTos);
    return this.http.get(`${this.configService.baseApiUrl}industry-scenarios/consolidation-report`, {
      params,
      responseType: 'blob'
    })
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.MultipleChoices:
        return throwError(errorResponse.error);
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error?.message) {
          errorMessage = errorResponse.error.message;
        }
        break;
      }
    }
    return throwError(errorMessage);
  }
}
